// Content for the Expérience numérique « Notre solution » section.
export type Bi = { fr: string; en: string }

export const DX_LOGOS = {
  bciti: 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png',
  atna: 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png',
}

export const dxSolutionCopy = {
  eyebrow: { fr: 'Notre solution', en: 'Our solution' },
  navy: [
    { fr: 'Tous les chemins', en: 'Every path' },
    { fr: 'mènent à une', en: 'leads to one' },
  ] as Bi[],
  indigo: { fr: 'même relation.', en: 'same relationship.' },
  body: {
    fr: 'Vos usagers communiquent avec vous de partout. BCITI | ATNA relie vos propres expériences numériques aux autres canaux pour préserver le contexte, reconnaître l’usager et poursuivre la relation jusqu’au service et à l’action.',
    en: 'Your users reach you from everywhere. BCITI | ATNA connects your own digital experiences to other channels to preserve context, recognize the user and carry the relationship through to service and action.',
  },
  benefits: [
    { fr: 'Une expérience cohérente pour vos usagers', en: 'A consistent experience for your users' },
    { fr: 'Un seul profil, un seul contexte, tous les canaux', en: 'One profile, one context, every channel' },
    { fr: 'Moins de ressaisies, plus d’efficacité pour vos équipes', en: 'Less duplicate data entry, more efficiency for your teams' },
  ] as Bi[],
  finalNavy: { fr: 'Un usager. Une relation.', en: 'One user. One relationship.' },
  finalIndigo: { fr: 'Peu importe le point d’entrée.', en: 'Whatever the entry point.' },
}

export type ExperienceKey = 'web' | 'app' | 'portal'

export const dxExperiences = {
  heading: { fr: 'Vos expériences BCITI | ATNA', en: 'Your BCITI | ATNA experiences' },
  sub: { fr: 'Clé en main, modernes et intégrées.', en: 'Turnkey, modern and integrated.' },
  items: [
    { key: 'web', label: { fr: 'Site Web intelligent', en: 'Intelligent website' }, text: { fr: 'Découvrir, comprendre et faire une demande', en: 'Discover, understand and make a request' } },
    { key: 'app', label: { fr: 'Application mobile', en: 'Mobile app' }, text: { fr: 'Accéder, recevoir, signaler et agir', en: 'Access, receive, report and act' } },
    { key: 'portal', label: { fr: 'Portail Mon espace', en: 'My Space portal' }, text: { fr: 'S’identifier et gérer vos services', en: 'Sign in and manage your services' } },
  ] as { key: ExperienceKey; label: Bi; text: Bi }[],
}

export type ChannelKey = 'email' | 'social' | 'call' | 'llm' | 'forms' | 'other'

export const dxChannels = {
  heading: { fr: 'Autres canaux d’interaction', en: 'Other interaction channels' },
  sub: { fr: 'Captez et unifiez toutes les conversations.', en: 'Capture and unify every conversation.' },
  items: [
    { key: 'email', label: { fr: 'Courriel', en: 'Email' }, text: { fr: 'Messages et pièces jointes', en: 'Messages and attachments' } },
    { key: 'social', label: { fr: 'Réseaux sociaux', en: 'Social media' }, text: { fr: 'Facebook, Instagram, etc.', en: 'Facebook, Instagram, etc.' } },
    { key: 'call', label: { fr: 'Centre d’appels', en: 'Call centre' }, text: { fr: 'Appels, demandes téléphoniques', en: 'Calls, phone requests' } },
    { key: 'llm', label: { fr: 'LLM / assistants IA', en: 'LLM / AI assistants' }, text: { fr: 'ChatGPT, Gemini, Copilot, etc.', en: 'ChatGPT, Gemini, Copilot, etc.' } },
    { key: 'forms', label: { fr: 'Formulaires externes', en: 'External forms' }, text: { fr: 'Intégration et capture', en: 'Integration and capture' } },
    { key: 'other', label: { fr: 'Autres canaux', en: 'Other channels' }, text: { fr: 'Guichets, événements, etc.', en: 'Service counters, events, etc.' } },
  ] as { key: ChannelKey; label: Bi; text: Bi }[],
}

export const dxPlatform = {
  line1: { fr: 'Une seule plateforme.', en: 'One platform.' },
  line2: { fr: 'De l’interaction à l’action.', en: 'From interaction to action.' },
}

export type FoundationKey = 'sso' | 'consent' | 'card' | 'notify' | 'requests' | 'data'

export const dxFoundation: { key: FoundationKey; label: Bi; text: Bi }[] = [
  { key: 'sso', label: { fr: 'Identité et SSO', en: 'Identity & SSO' }, text: { fr: 'Accès unique et sécurisé', en: 'Single, secure access' } },
  { key: 'consent', label: { fr: 'Profil et consentements', en: 'Profile & consents' }, text: { fr: 'Vos informations, sous votre contrôle', en: 'Your information, under your control' } },
  { key: 'card', label: { fr: 'Carte numérique', en: 'Digital card' }, text: { fr: 'Accès aux services et avantages', en: 'Access to services and benefits' } },
  { key: 'notify', label: { fr: 'Avis et notifications', en: 'Notices & notifications' }, text: { fr: 'Voix, SMS, courriel', en: 'Voice, SMS, email' } },
  { key: 'requests', label: { fr: 'Requêtes et signalements', en: 'Requests & reports' }, text: { fr: 'Saisie, suivi et délais de service (SLA)', en: 'Capture, tracking and SLA' } },
  { key: 'data', label: { fr: 'Données et tableaux de bord', en: 'Data & dashboards' }, text: { fr: 'Vue en temps réel pour vos équipes', en: 'Real-time view for your teams' } },
]

export const dxAtnaChecks: Bi[] = [
  { fr: 'Comprend l’intention', en: 'Understands intent' },
  { fr: 'Vérifie les règles et le contexte', en: 'Verifies rules and context' },
  { fr: 'Recommande la meilleure action', en: 'Recommends the best action' },
  { fr: 'Orchestre les systèmes', en: 'Orchestrates systems' },
  { fr: 'Agit selon vos autorisations', en: 'Acts within your authorizations' },
]

export type ServiceKey = 'requests' | 'permits' | 'leisure' | 'payments' | 'licences' | 'comms' | 'water' | 'systems'

export const dxServices = {
  heading: { fr: 'Services et opérations', en: 'Services & operations' },
  sub: { fr: 'Connectés à vos systèmes et processus existants.', en: 'Connected to your existing systems and processes.' },
  items: [
    { key: 'requests', title: { fr: 'Requêtes', en: 'Requests' }, text: { fr: 'et signalements', en: 'and reports' } },
    { key: 'permits', title: { fr: 'Permis', en: 'Permits' }, text: { fr: 'et inspections', en: 'and inspections' } },
    { key: 'leisure', title: { fr: 'Loisirs', en: 'Recreation' }, text: { fr: 'et activités', en: 'and activities' } },
    { key: 'payments', title: { fr: 'Paiements', en: 'Payments' }, text: { fr: 'et transactions', en: 'and transactions' } },
    { key: 'licences', title: { fr: 'Licences', en: 'Licences' }, text: { fr: 'et enregistrements', en: 'and registrations' } },
    { key: 'comms', title: { fr: 'Communications', en: 'Resident' }, text: { fr: 'citoyennes', en: 'communications' } },
    { key: 'water', title: { fr: 'Compteurs d’eau', en: 'Water meters' }, text: { fr: 'et services techniques', en: 'and technical services' } },
    { key: 'systems', title: { fr: 'Systèmes existants', en: 'Existing systems' }, text: { fr: '(API, ERP, etc.)', en: '(API, ERP, etc.)' } },
  ] as { key: ServiceKey; title: Bi; text: Bi }[],
}
